/**
 * The figure service (plots plan D2): one batched request per tab draws every
 * requested question for the (filtered) sample and each group of one cut, in
 * the plot contract the report precomputes (`CanonicalAnalysisOutput.figures`).
 * Python owns all arithmetic; Nest resolves the study, rows and saved-segment
 * membership and caches the response.
 */

/**
 * What splits or filters the sample: the analysis cuts' split kinds, plus
 * `assigned_asset` (which asset each twin was shown; source `assigned-asset`,
 * values are asset ids) and `population_column` (one curated population
 * column of the report's allowlist; source is the column, values its levels).
 */
export type StudyFigureSplitKind =
  | 'single_select'
  | 'multi_select'
  | 'rating_band'
  | 'audience_attribute'
  | 'saved_segment'
  | 'assigned_asset'
  | 'population_column';

export interface StudyFigureCutGroup {
  groupId: string;
  label?: string;
  /** Option values, rating scores, attribute levels or saved-segment ids. */
  values: Array<string>;
}

/**
 * One cut. `source` is a question id, an attribute axis id
 * (`ChartSegmentAxis.id`), `audience-tags` for saved segments or
 * `assigned-asset`. `groups` omitted: one group per option, level, saved
 * segment or assigned asset.
 */
export interface StudyFigureCut {
  kind: StudyFigureSplitKind;
  source: string;
  groups?: Array<StudyFigureCutGroup>;
}

/**
 * Keep the rows in any of `values`; `exclude` keeps the rows outside them
 * instead (a saved segment's everyone else). Filters combine with AND.
 */
export interface StudyFigureFilter {
  kind: StudyFigureSplitKind;
  source: string;
  values: Array<string>;
  exclude?: boolean;
}

/** Producer options (plan "Rule defaults"); none are declared yet. */
export type StudyFigureProducerOptions = Record<string, never>;

export interface StudyFiguresRequest {
  /** Generation timestamp of the displayed report; a stale one is a 409. */
  reportRevision?: string;
  /** The tab's questions; absent draws every plotted question. */
  questionIds?: Array<string>;
  cut?: StudyFigureCut;
  filters?: Array<StudyFigureFilter>;
  producerOptions?: StudyFigureProducerOptions;
  /** The viewer's UI language: plot chrome resolves in it. */
  locale?: string;
}

export interface StudyFigureGroup {
  /** Plot key: `total`, or `cut:<groupId>`. */
  id: string;
  /** The cut group's own id (option value, level, segment id); null for Total. */
  sourceId: string | null;
  label: string | null;
  kind: 'total' | 'cut';
  n: number;
  lowBase: boolean;
  /** False when nobody is in the group or a budget cap dropped it (see `notes`). */
  drawn: boolean;
  /**
   * Why a group with people in it is not drawn: `too_few`, a filtered sample
   * or a cut group under the figure floor (10, the theme cards' floor). The
   * unfiltered whole sample is always drawn. Absent otherwise.
   */
  reason?: 'too_few';
  /** `other`: a population cut's folded Other group (its smallest levels). */
  role?: 'other';
  // A drawn group can still lack a plot on one question: a non-segmentable
  // question is drawn for Total only, so read `plots[group.id]` as optional.
}

export interface StudyFigureQuestion {
  questionId: string;
  metricIds: Array<string>;
  kind: string;
  /**
   * Group id -> registry plot `{kind, data}`. Total is always present, except
   * on `theme-cards` (empty: the figures ride in `themeCards`); a cut group is
   * absent on a non-segmentable question even when it is drawn.
   */
  plots: Record<string, { kind: string; data: Record<string, unknown> }>;
  /**
   * Figure service only (Wave 7 S3), TS twin only (no pydantic model): a
   * single-series choice question under a cut as one `grouped-bars` plot,
   * options x drawn cut groups, reshaped by Python from the groups' own
   * choice-bars plots and checked by `validate_plot_payload`. Absent without a
   * cut, on a multi-series read (see `cutPlots`) or when it cannot be formed.
   */
  cutPlot?: { kind: string; data: Record<string, unknown> };
  /**
   * Figure service only (Wave 7 S3), TS twin only: a multi-series (lane)
   * choice question under a cut, one `grouped-bars` plot per lane in Total's
   * lane order (`seriesId` / `label` are the lane's), each checked by
   * `validate_plot_payload`. All lanes or none.
   */
  cutPlots?: Array<StudyFigureLaneCutPlot>;
  /**
   * Figure service only, TS twin only (open-end themes plan): a themed open
   * end (`kind: 'theme-cards'`) prints each theme's figure inside its card,
   * every number stamped by Python (`theme_figures.theme_cards`). The card
   * joins the words by `themeId` and computes nothing.
   */
  themeCards?: StudyFigureThemeCards;
}

/** `ok` at 30 or more, `thin` 10 to 29 (muted, never marked), `too_few` under 10 (no figure). */
export type StudyFigureThemeBaseState = 'ok' | 'thin' | 'too_few';

export interface StudyFigureThemeCards {
  /** 'pooled': one figure per theme; 'versions': one cell per version, no pooled figure. */
  layout: 'pooled' | 'versions';
  /** Coded twins the pooled figure is of (pooled layout only). */
  pooledBase: number | null;
  pooledBaseState: StudyFigureThemeBaseState | null;
  /** Versions in the study's order (versions layout only). */
  versions: Array<StudyFigureThemeVersion>;
  /** Themes in display order: whole-sample share desc, Other last (same order under a filter). */
  themes: Array<StudyFigureThemeCard>;
  /** Every real theme at 90% or more in every version: nothing is marked. */
  isSaturated: boolean;
  /** An Other theme exists (the (i) adds its sentence). */
  hasOther: boolean;
}

export interface StudyFigureThemeVersion {
  versionId: string;
  /** The version's name as the study names it ("Chose X" on a reason-for-choice); null: unnamed. */
  label: string | null;
  /** Coded twins on this version in the view. */
  base: number;
  baseState: StudyFigureThemeBaseState;
}

export interface StudyFigureThemeVersionCell {
  versionId: string;
  /** Whole percent, stamped; null when the base is too few. */
  percent: number | null;
}

export interface StudyFigureThemeCard {
  themeId: string;
  isOther: boolean;
  /** Whole percent, stamped (null when the base is too few). Pooled layout only. */
  percent: number | null;
  /** One per `versions[]` entry, same order (versions layout only). */
  versions: Array<StudyFigureThemeVersionCell>;
  /** The one version marked "Higher", or null. */
  higherVersionId: string | null;
  /** The version it is clearly higher than (the runner-up). */
  higherThanVersionId: string | null;
}

export interface StudyFigureLaneCutPlot {
  seriesId: string;
  label: string | null;
  plot: { kind: string; data: Record<string, unknown> };
}

/**
 * A population column drawn as a figure (Wave 7 S5) rides in the batch under
 * the question id `population:<measure>:<column>`: `categorical` draws its
 * levels as choice bars, `numeric` its numbers as a histogram. Nest serves it
 * to the owner only, for a column of the report's curated allowlist.
 */
export type StudyFigurePopulationMeasure = 'categorical' | 'numeric';

export const STUDY_FIGURE_POPULATION_PREFIX = 'population:';

/** The longest question id the figures route accepts (the request DTO's limit). */
export const STUDY_FIGURE_QUESTION_ID_MAX_LENGTH = 200;

export function populationFigureId(measure: StudyFigurePopulationMeasure, column: string): string {
  return `${STUDY_FIGURE_POPULATION_PREFIX}${measure}:${column}`;
}

/** The `{ measure, column }` of a population figure id, else null. */
export function parsePopulationFigureId(
  questionId: string,
): { measure: StudyFigurePopulationMeasure; column: string } | null {
  if (!questionId.startsWith(STUDY_FIGURE_POPULATION_PREFIX)) {
    return null;
  }
  const rest = questionId.slice(STUDY_FIGURE_POPULATION_PREFIX.length);
  const separator = rest.indexOf(':');
  const measure = separator > 0 ? rest.slice(0, separator) : '';
  const column = separator > 0 ? rest.slice(separator + 1) : '';
  if ((measure !== 'categorical' && measure !== 'numeric') || column.length === 0) {
    return null;
  }
  return { measure, column };
}

/**
 * A question id the figures route can never serve as a population figure: it
 * names one (the prefix) but is malformed or longer than the route accepts.
 * The client never asks for one (that card keeps its own chart) and Nest
 * leaves one out, so it never refuses the batch it rides in.
 */
export function isUnservablePopulationFigureId(questionId: string): boolean {
  return (
    questionId.startsWith(STUDY_FIGURE_POPULATION_PREFIX) &&
    (questionId.length > STUDY_FIGURE_QUESTION_ID_MAX_LENGTH || parsePopulationFigureId(questionId) == null)
  );
}

export interface StudyFigureNote {
  /**
   * `paired_base` (figure service only): the before half of a pair counts
   * complete pairs; `label` is the note in the request's UI locale.
   * `cut_groups_folded` (figure service only): a population cut folded its
   * smallest levels (`foldedGroups`) into one Other group; `label` likewise.
   */
  code: 'segments_dropped' | 'total_over_budget' | 'question_invalid' | 'paired_base' | 'cut_groups_folded';
  reason?: 'compute_groups_cap' | 'unit_groups_cap' | 'bytes_cap';
  segmentIds?: Array<string>;
  unitGroups?: number;
  bytes?: number;
  questionId?: string;
  errorClass?: string;
  label?: string;
  foldedGroups?: number;
}

export interface StudyFiguresView {
  version: 'figure-service.v1';
  figuresVersion: 'figures.v1';
  locale: string | null;
  cut: StudyFigureCut | null;
  filters: Array<StudyFigureFilter>;
  /** Total first, then the cut's groups in cut order. */
  groups: Array<StudyFigureGroup>;
  questions: Array<StudyFigureQuestion>;
  notes: Array<StudyFigureNote>;
}

/** One group a listed cut forms on the whole sample. */
export interface StudyFiguresMetaValue {
  /** The group's id in a figures response (`StudyFigureGroup.sourceId`). */
  id: string;
  label: string;
  n: number;
  /** True for the split's complement of its one named group (everyone else). */
  complement: boolean;
  /** `other`: a population cut's folded Other group; its filter keeps every level it holds. */
  role?: 'other';
  /** The filter that keeps this group (a lane-merged card filters, then unfolds). */
  filter: StudyFigureFilter;
}

export interface StudyFiguresMetaCut {
  cut: StudyFigureCut;
  values: Array<StudyFiguresMetaValue>;
}

export interface StudyFiguresMetaQuestion extends StudyFiguresMetaCut {
  questionId: string;
}

export interface StudyFiguresMetaSegment extends StudyFiguresMetaCut {
  segmentId: string;
  label: string;
}

export interface StudyFiguresMetaPopulationCut extends StudyFiguresMetaCut {
  column: string;
  label: string;
}

/**
 * Where the tab's Population group comes from: the report's curated columns
 * (`allowlist`), none curated for this study (`none`), curated columns none of
 * which forms a cut on these answers (`none_formable`), the population could
 * not be read (`unavailable`; the tab says which, never falling back to
 * other attributes), or not offered at all (`share`: a shared report carries
 * no audience cuts).
 */
export type StudyFiguresPopulationSource =
  | 'allowlist'
  | 'none'
  | 'none_formable'
  | 'unavailable'
  | 'share';

/**
 * The cuts the survey tab may offer (plots plan D3): only cuts the figures
 * route forms without a refusal, each with its groups' sizes.
 */
export interface StudyFiguresMeta {
  version: 'figure-meta.v1';
  /** Generation timestamp of the report the meta was read from. */
  reportRevision: string | null;
  n: number;
  questions: Array<StudyFiguresMetaQuestion>;
  savedSegments: Array<StudyFiguresMetaSegment>;
  /**
   * One cut that breaks by every listed saved segment, one group each, largest
   * first. Null when fewer than two segments qualify, and for a share viewer.
   * Absent on a meta payload from before this field.
   */
  allSavedSegments?: StudyFiguresMetaCut | null;
  population: Array<StudyFiguresMetaPopulationCut>;
  populationSource: StudyFiguresPopulationSource;
  assignedAsset: StudyFiguresMetaCut | null;
}
